// Typed wrappers around the Postgres function API and the Worker endpoints.
// Rows from the database are mapped into strict domain types here, once.

import type { PostgrestError } from '@supabase/supabase-js';
import type { Database } from '../../shared/database.types';
import type { DevLocationRequest, VerifyLocationRequest, VerifyLocationResponse } from '../../shared/location-api';
import { ApiError, isKnownCode } from './errors';
import { supabase } from './supabase';

type Enums = Database['public']['Enums'];
type Composite = Database['public']['CompositeTypes'];
type Fn<Name extends keyof Database['public']['Functions']> = Database['public']['Functions'][Name];

export type Category = Enums['post_category'];
export type Role = Enums['user_role'];
export type AccountStatus = Enums['account_status'];
export type ReportReason = Enums['report_reason'];
export type ReportStatus = Enums['report_status'];

export interface Me {
  id: string;
  displayName: string | null;
  bio: string;
  avatarHue: number;
  role: Role;
  status: AccountStatus;
  suspendedUntil: string | null;
  statusReason: string | null;
  createdAt: string;
  location: { placeName: string; verifiedAt: string; nextChangeAt: string; simulated: boolean } | null;
}

export interface Author {
  id: string;
  name: string | null;
  hue: number;
  isNeighbor: boolean;
}

export interface Photo {
  id: string;
  width: number;
  height: number;
}

export interface Post {
  id: string;
  author: Author & { place: string | null };
  category: Category | null;
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  isDeleted: boolean;
  isRemoved: boolean;
  replyCount: number;
  neighborCount: number;
  thanksCount: number;
  viewerThanked: boolean;
  isOwn: boolean;
  photos: Photo[];
}

export interface Reply {
  id: string;
  postId: string;
  parentId: string | null;
  author: Author;
  body: string | null;
  createdAt: string;
  editedAt: string | null;
  hiddenReason: 'deleted' | 'removed' | 'blocked' | null;
  isOwn: boolean;
}

export interface Profile {
  id: string;
  displayName: string;
  bio: string;
  avatarHue: number;
  placeName: string | null;
  isVerified: boolean;
  isNeighbor: boolean;
  joinedAt: string;
  blockedByMe: boolean;
  isSelf: boolean;
}

export type ModReport = Fn<'mod_list_reports'>['Returns'][number];
export type ModAction = Fn<'mod_list_actions'>['Returns'][number];
export type ModMember = Fn<'mod_list_members'>['Returns'][number];
export type MemberGeo = Fn<'admin_get_member_geo'>['Returns'][number];
export type LocationCheck = Fn<'admin_list_location_checks'>['Returns'][number];

// ---------------------------------------------------------------------------

function toApiError(error: PostgrestError | Error): ApiError {
  const message = error.message ?? '';
  if (isKnownCode(message)) return new ApiError(message);
  if (/fetch|network/i.test(message)) return new ApiError('network', message);
  return new ApiError('unknown', message);
}

async function rpc<T>(request: PromiseLike<{ data: T | null; error: PostgrestError | null }>): Promise<T> {
  let result: { data: T | null; error: PostgrestError | null };
  try {
    result = await request;
  } catch (error) {
    throw toApiError(error as Error);
  }
  if (result.error) throw toApiError(result.error);
  return result.data as T;
}

function toPost(row: Composite['post_item']): Post {
  return {
    id: row.id!,
    author: {
      id: row.author_id!,
      name: row.author_name,
      hue: row.author_hue ?? 30,
      place: row.author_place,
      isNeighbor: row.author_is_neighbor ?? false,
    },
    category: row.category,
    body: row.body,
    createdAt: row.created_at!,
    editedAt: row.edited_at,
    isDeleted: row.is_deleted ?? false,
    isRemoved: row.is_removed ?? false,
    replyCount: row.reply_count ?? 0,
    neighborCount: row.neighbor_count ?? 0,
    thanksCount: row.thanks_count ?? 0,
    viewerThanked: row.viewer_thanked ?? false,
    isOwn: row.is_own ?? false,
    photos: (row.photos as Photo[] | null) ?? [],
  };
}

function toReply(row: Composite['comment_item']): Reply {
  return {
    id: row.id!,
    postId: row.post_id!,
    parentId: row.parent_id,
    author: {
      id: row.author_id!,
      name: row.author_name,
      hue: row.author_hue ?? 30,
      isNeighbor: row.author_is_neighbor ?? false,
    },
    body: row.body,
    createdAt: row.created_at!,
    editedAt: row.edited_at,
    hiddenReason: row.hidden_reason as Reply['hiddenReason'],
    isOwn: row.is_own ?? false,
  };
}

// --- Me & profiles -----------------------------------------------------------

export async function getMe(): Promise<Me | null> {
  const rows = await rpc(supabase.rpc('get_me'));
  const row = rows[0];
  if (!row) return null;
  return {
    id: row.id,
    displayName: row.display_name,
    bio: row.bio,
    avatarHue: row.avatar_hue,
    role: row.role,
    status: row.status,
    suspendedUntil: row.suspended_until,
    statusReason: row.status_reason,
    createdAt: row.created_at,
    location: row.place_name
      ? {
          placeName: row.place_name,
          verifiedAt: row.verified_at,
          nextChangeAt: row.next_change_at,
          simulated: row.location_simulated,
        }
      : null,
  };
}

export function updateMyProfile(displayName: string, bio: string) {
  return rpc(supabase.rpc('update_my_profile', { p_display_name: displayName, p_bio: bio }));
}

export async function getProfile(userId: string): Promise<Profile> {
  const rows = await rpc(supabase.rpc('get_profile', { p_user_id: userId }));
  const row = rows[0];
  if (!row) throw new ApiError('not_found');
  return {
    id: row.id,
    displayName: row.display_name,
    bio: row.bio,
    avatarHue: row.avatar_hue,
    placeName: row.place_name,
    isVerified: row.is_verified,
    isNeighbor: row.is_neighbor,
    joinedAt: row.joined_at,
    blockedByMe: row.blocked_by_me,
    isSelf: row.is_self,
  };
}

export async function getNearbySummary() {
  const rows = await rpc(supabase.rpc('get_nearby_summary'));
  return { neighborCount: rows[0]?.neighbor_count ?? 0, postsThisWeek: rows[0]?.posts_this_week ?? 0 };
}

// --- Feed & threads ------------------------------------------------------------

export const FEED_PAGE_SIZE = 20;

export interface FeedCursor {
  before: string;
  beforeId: string;
}

export interface FeedFilter {
  category?: Category | null;
  authorId?: string | null;
}

export async function getFeed(filter: FeedFilter, cursor?: FeedCursor | null, limit = FEED_PAGE_SIZE): Promise<Post[]> {
  const rows = await rpc(
    supabase.rpc('get_feed', {
      p_before: cursor?.before,
      p_before_id: cursor?.beforeId,
      p_limit: limit,
      p_category: filter.category ?? undefined,
      p_author_id: filter.authorId ?? undefined,
    }),
  );
  return rows.map(toPost);
}

export async function getPost(postId: string): Promise<Post> {
  const rows = await rpc(supabase.rpc('get_post', { p_post_id: postId }));
  if (!rows[0]) throw new ApiError('not_found');
  return toPost(rows[0]);
}

export async function getReplies(postId: string): Promise<Reply[]> {
  const rows = await rpc(supabase.rpc('get_comments', { p_post_id: postId }));
  return rows.map(toReply);
}

export function createPost(body: string, category: Category | null, photoIds: string[] = []) {
  return rpc(
    supabase.rpc('create_post', {
      p_body: body,
      p_category: category ?? undefined,
      p_photo_ids: photoIds.length ? photoIds : undefined,
    }),
  );
}

export function editPost(postId: string, body: string, category: Category | null) {
  return rpc(supabase.rpc('edit_post', { p_post_id: postId, p_body: body, p_category: category ?? undefined }));
}

export function deletePost(postId: string) {
  return rpc(supabase.rpc('delete_post', { p_post_id: postId }));
}

export function createReply(postId: string, body: string, parentId: string | null) {
  return rpc(supabase.rpc('create_comment', { p_post_id: postId, p_body: body, p_parent_id: parentId ?? undefined }));
}

export function editReply(replyId: string, body: string) {
  return rpc(supabase.rpc('edit_comment', { p_comment_id: replyId, p_body: body }));
}

export function deleteReply(replyId: string) {
  return rpc(supabase.rpc('delete_comment', { p_comment_id: replyId }));
}

export function setThanks(postId: string, thanked: boolean) {
  return rpc(supabase.rpc('set_thanks', { p_post_id: postId, p_thanked: thanked }));
}

// --- Blocking & reporting ------------------------------------------------------------

export function blockUser(userId: string) {
  return rpc(supabase.rpc('block_user', { p_user_id: userId }));
}

export function unblockUser(userId: string) {
  return rpc(supabase.rpc('unblock_user', { p_user_id: userId }));
}

export function listMyBlocks() {
  return rpc(supabase.rpc('list_my_blocks'));
}

export type ReportTarget = { postId: string } | { replyId: string } | { userId: string };

export function createReport(target: ReportTarget, reason: ReportReason, details: string) {
  return rpc(
    supabase.rpc('create_report', {
      p_reason: reason,
      p_details: details,
      p_post_id: 'postId' in target ? target.postId : undefined,
      p_comment_id: 'replyId' in target ? target.replyId : undefined,
      p_user_id: 'userId' in target ? target.userId : undefined,
    }),
  );
}

// --- Moderation ---------------------------------------------------------------------

export function modListReports(status: ReportStatus | null, userId?: string) {
  return rpc(supabase.rpc('mod_list_reports', { p_status: status ?? undefined, p_user_id: userId }));
}

export function modListActions(userId?: string) {
  return rpc(supabase.rpc('mod_list_actions', { p_user_id: userId }));
}

export function modListMembers(query?: string) {
  return rpc(supabase.rpc('mod_list_members', { p_query: query }));
}

export function modDismissReport(reportId: string, note: string) {
  return rpc(supabase.rpc('mod_dismiss_report', { p_report_id: reportId, p_note: note }));
}

export function modSetContentRemoved(
  target: { postId: string } | { replyId: string },
  removed: boolean,
  reason: string,
  reportId?: string,
) {
  return rpc(
    supabase.rpc('mod_set_content_removed', {
      p_removed: removed,
      p_reason: reason,
      p_post_id: 'postId' in target ? target.postId : undefined,
      p_comment_id: 'replyId' in target ? target.replyId : undefined,
      p_report_id: reportId,
    }),
  );
}

export function modSetUserStatus(
  userId: string,
  status: AccountStatus,
  reason: string,
  suspendDays?: number,
  reportId?: string,
) {
  return rpc(
    supabase.rpc('mod_set_user_status', {
      p_user_id: userId,
      p_status: status,
      p_reason: reason,
      p_suspend_days: suspendDays,
      p_report_id: reportId,
    }),
  );
}

export function adminSetRole(userId: string, role: Role) {
  return rpc(supabase.rpc('admin_set_role', { p_user_id: userId, p_role: role }));
}

export function adminGetMemberGeo(userId?: string) {
  return rpc(supabase.rpc('admin_get_member_geo', { p_user_id: userId }));
}

export function adminListLocationChecks(userId?: string) {
  return rpc(supabase.rpc('admin_list_location_checks', { p_user_id: userId }));
}

export function adminResetLocationCooldown(userId: string, reason: string) {
  return rpc(supabase.rpc('admin_reset_location_cooldown', { p_user_id: userId, p_reason: reason }));
}

// --- Worker endpoints ------------------------------------------------------------------

async function workerPost<T>(path: string, body: unknown, contentType = 'application/json'): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new ApiError('not_authenticated');
  let res: Response;
  try {
    res = await fetch(path, {
      method: 'POST',
      headers: { 'content-type': contentType, authorization: `Bearer ${token}` },
      body: contentType === 'application/json' ? JSON.stringify(body) : (body as BodyInit),
    });
  } catch (error) {
    throw new ApiError('network', (error as Error).message);
  }
  const payload = (await res.json().catch(() => ({ ok: false, code: 'server_error' }))) as T;
  return payload;
}

export async function verifyLocation(request: VerifyLocationRequest) {
  const result = await workerPost<VerifyLocationResponse>('/api/location/verify', request);
  if (!result.ok) throw new ApiError(result.code, result.nextChangeAt ?? undefined);
  return result;
}

export async function devSetLocation(request: DevLocationRequest) {
  const result = await workerPost<VerifyLocationResponse>('/api/dev/location', request);
  if (!result.ok) throw new ApiError(result.code);
  return result;
}

type WorkerResult<T> = ({ ok: true } & T) | { ok: false; code: string };

/** Uploads one already-resized JPEG. Returns the new photo's id (not yet attached to a note). */
export async function uploadPhoto(jpeg: Blob): Promise<Photo> {
  const result = await workerPost<WorkerResult<Photo>>('/api/photos', jpeg, 'image/jpeg');
  if (!result.ok) throw new ApiError(result.code);
  return { id: result.id, width: result.width, height: result.height };
}

/** Signed, short-lived URLs for the photos the viewer may see (others are omitted). */
export async function getPhotoUrls(ids: string[]): Promise<Record<string, string>> {
  const result = await workerPost<WorkerResult<{ urls: Record<string, string> }>>('/api/photos/urls', { ids });
  if (!result.ok) throw new ApiError(result.code);
  return result.urls;
}

/** Permanently deletes the signed-in member's account and everything they created. */
export async function deleteMyAccount(): Promise<void> {
  const result = await workerPost<WorkerResult<object>>('/api/account/delete', { confirm: true });
  if (!result.ok) throw new ApiError(result.code);
}
